import { RootState } from "@/app/store";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { logout } from "@/features/auth/AuthSlice"
import { toast } from "@/components/ui/UseToast";

// base url
const { VITE_API_URL } = import.meta.env;
interface FastApiError {
  detail?: string | string[]; // FastAPI's detail field can be a string or an array of strings
  message?: string | string[]; // FastAPI's detail field can be a string or an array of strings
}
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;
  if (state.auth.skipRequests) {
    return {
      error: {
        status: 401,
        data: { message: "Session expired. Requests are skipped." },
      },
    };
  }
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
    toast({
      variant: "destructive",
      title: "Session Expired",
      description: "Your session has expired. Please log in again to continue.",
      style: { width: "350px" }, // Set the desired width here
    });
  }

  if (result.error) {
    const { status, data } = result.error as FetchBaseQueryError & {
      data?: FastApiError;
    };

    // Check for a 422 Unprocessable Entity or other specific errors
    if (status === 422 && data && typeof data === "object") {
      const errorDetail = Array.isArray(data.detail)
        ? data.detail.join(", ")
        : (data.detail ?? "Unprocessable entity");
      result.error.data = { message: errorDetail };
    } else {
      // For other errors, keep the default behavior
      result.error.data = {
        message:
          (data as FastApiError)?.detail || "An unexpected error occurred",
      };
    }
  }
  return result;
};

const baseQuery = fetchBaseQuery({
  baseUrl: VITE_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.userToken;

    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("Access-Control-Allow-Origin", `*`);
    headers.set("Accept", "application/json");
    return headers;
  },
});

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes: [
    "Common",
    "Roles",
    "Staffs",
    "Organization",
    "WaitingList",
    "serveticket",
    "kiosk",
    "User",
    "Counters",
    "Service",
    "Department",
    "Ticket",
    "LiveQueue",
    "Tasks",
    "WorkFlowBuilder",
    "FormBuilder"
  ],
});
